import React, { useEffect, useState } from "react";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { getJwtToken, END_POINT } from "../../Redux/AdminReducer/action";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import RightWrapper from "../../Components/CommonComponents/ComponentWrapper";
import ContactUsPopup from "./ContactUsEmail";
import { useSelector } from "react-redux";
import {
  BsArrowLeft,
  BsCheckCircleFill,
  BsCalendarCheck,
  BsCreditCard,
  BsPerson,
  BsBuilding,
  BsClipboard,
  BsCheck,
  BsSearch,
  BsHeadset,
  BsArrowRepeat,
  BsLightningChargeFill,
  BsInfoCircle,
  BsCloudDownload,
} from "react-icons/bs";
import { FaCrown, FaCalendarAlt, FaPrint } from "react-icons/fa";
import { MdOutlineSecurity, MdVerified, MdOutlineLock } from "react-icons/md";
import { PLAN_SECTIONS } from "../../constants/planSections";

export const fetchSubscriptionDetails = async () => {
  try {
    const res = await axios.get(`${END_POINT}/mainDoctor/subscription-details`, {
      headers: {
        Authorization: getJwtToken(),
      },
    });
    return res?.data;
  } catch (error) {
    console.error("Error fetching subscription details:", error);
    throw error;
  }
};

const formatDate = (dateStr) => {
  if (!dateStr) return "N/A";
  try {
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return String(dateStr);
    return d.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  } catch (e) {
    return String(dateStr);
  }
};

const categorizeSection = (value) => {
  const v = (value || "").toLowerCase();
  if (["dashboard", "enquiry", "appointment", "prescription", "patients", "courier", "vitals-templates"].includes(v)) {
    return "Core Clinic & Patients";
  }
  if (["staffprofile", "doctors", "assistantdoctor", "receptionist", "consultant", "clinic-profile", "login-profile"].includes(v)) {
    return "Staff & Profile";
  }
  if (["attendancecount", "attendancerequests", "leavecalendar", "attendancecalendar", "calendar", "certificates"].includes(v)) {
    return "Operations & Attendance";
  }
  if (["alternativetherapies", "bids", "transaction", "income_expenses", "referral-business", "main", "graphs"].includes(v)) {
    return "Finance & Inventory";
  }
  if (["contactus", "callback", "questions", "testimonials", "sharedthoughts", "enrolledinhealthpackages", "calmyoureczemanaturally", "bookalternativetherapies", "dropdowns", "blogs", "casestudies", "onlineforms", "site-config"].includes(v)) {
    return "Interactions & Configuration";
  }
  return "Public & Web Modules";
};

export const SubscriptionPlanView = ({ embedded = false }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [copiedField, setCopiedField] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("All");
  const [isContactOpen, setIsContactOpen] = useState(false);
  const [syncing, setSyncing] = useState(false);

  const loggedInUser = useSelector((state) => state?.AuthReducer?.userLogindata?.data);

  const loadDetails = async () => {
    setLoading(true);
    try {
      const result = await fetchSubscriptionDetails();
      setData(result);
    } catch (err) {
      toast.error(err?.response?.data?.error || "Could not load subscription details");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadDetails();
  }, []);

  // Pulls the latest plan definitions from the landing site into this clinic's
  // PlanConfigs in one call, so nothing has to be fetched on every login. The
  // plan is read at login and carried in the token, so a user picks up new
  // features when they next log in rather than immediately.
  const handleSyncPlans = async () => {
    setSyncing(true);
    try {
      const res = await axios.post(
        `${END_POINT}/mainDoctor/sync-plans`,
        {},
        { headers: { Authorization: getJwtToken() } }
      );
      toast.success(res?.data?.message || "Plans synced");
      await loadDetails();
    } catch (err) {
      toast.error(
        err?.response?.data?.error || "Could not sync plans from the landing site"
      );
    } finally {
      setSyncing(false);
    }
  };

  const handleCopy = (text, fieldKey) => {
    if (!text) return;
    navigator.clipboard.writeText(text);
    setCopiedField(fieldKey);
    toast.success("Copied to clipboard!");
    setTimeout(() => {
      setCopiedField(null);
    }, 2000);
  };

  const handlePrint = () => {
    window.print();
  };

  const sub = data?.subscription;
  const doctor = data?.doctor;
  const tenantId = data?.tenant_id || "N/A";
  const plan = sub?.plan_details || {};
  const status = sub?.status || "active";
  const enabledSections = sub?.enabled_sections || [];

  // Calculate percentage of time elapsed
  let progressPercentage = 100;
  if (sub?.valid_from && sub?.valid_till) {
    const start = new Date(sub.valid_from).getTime();
    const end = new Date(sub.valid_till).getTime();
    const now = Date.now();
    if (end > start) {
      const total = end - start;
      const elapsed = Math.max(0, now - start);
      progressPercentage = Math.min(100, Math.max(0, Math.round(((total - elapsed) / total) * 100)));
    }
  }

  // Filter sections
  const filteredSections = PLAN_SECTIONS.filter((sec) => {
    const matchesSearch =
      sec.label.toLowerCase().includes(searchTerm.toLowerCase()) ||
      sec.value.toLowerCase().includes(searchTerm.toLowerCase());
    const cat = categorizeSection(sec.value);
    const matchesCat = selectedCategory === "All" || cat === selectedCategory;
    return matchesSearch && matchesCat;
  });

  const categories = [
    "All",
    "Core Clinic & Patients",
    "Staff & Profile",
    "Operations & Attendance",
    "Finance & Inventory",
    "Interactions & Configuration",
    "Public & Web Modules",
  ];

  const enabledCount = PLAN_SECTIONS.filter((s) => enabledSections.includes(s.value)).length;

  const content = (
    <div className="space-y-6 pb-8">
      {/* Header Bar */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 border-b border-slate-200 pb-4">
        <div className="flex items-center gap-3">
          {!embedded && (
            <button
              type="button"
              onClick={() => navigate(-1)}
              className="flex items-center gap-1.5 bg-slate-800 hover:bg-slate-700 text-white text-xs md:text-sm font-medium py-1.5 px-3 rounded-lg shadow-sm transition cursor-pointer"
            >
              <BsArrowLeft className="w-4 h-4" />
              <span>Back</span>
            </button>
          )}
          <div>
            <div className="flex items-center gap-2">
              <h1 className="text-xl md:text-2xl font-bold text-slate-900 leading-tight">
                Current Subscription Plan
              </h1>
              <span
                className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-semibold ${
                  status === "active"
                    ? "bg-emerald-100 text-emerald-800 border border-emerald-200"
                    : status === "expired"
                    ? "bg-rose-100 text-rose-800 border border-rose-200"
                    : "bg-amber-100 text-amber-800 border border-amber-200"
                }`}
              >
                <span
                  className={`w-2 h-2 rounded-full ${
                    status === "active" ? "bg-emerald-500 animate-pulse" : "bg-rose-500"
                  }`}
                />
                <span className="capitalize">{status}</span>
              </span>
            </div>
            <p className="text-xs text-slate-500 mt-0.5">
              Review your clinic's tenant subscription tier, validity dates, billing info, and enabled modules.
            </p>
          </div>
        </div>

        {/* Header Action Buttons */}
        <div className="flex items-center gap-2 self-start sm:self-auto">
          <button
            type="button"
            onClick={loadDetails}
            disabled={loading}
            title="Refresh Details"
            className="inline-flex items-center gap-1.5 bg-white hover:bg-slate-50 text-slate-700 border border-slate-200 text-xs md:text-sm font-medium py-2 px-3 rounded-lg shadow-xs transition cursor-pointer"
          >
            <BsArrowRepeat className={`w-3.5 h-3.5 ${loading ? "animate-spin text-sky-600" : ""}`} />
            <span>Refresh</span>
          </button>
          <button
            type="button"
            onClick={handleSyncPlans}
            disabled={syncing}
            title="Pull the latest plan features from the landing site"
            className="inline-flex items-center gap-1.5 bg-white hover:bg-slate-50 text-slate-700 border border-slate-200 text-xs md:text-sm font-medium py-2 px-3 rounded-lg shadow-xs transition cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed"
          >
            <BsCloudDownload className={`w-3.5 h-3.5 ${syncing ? "animate-pulse text-sky-600" : "text-slate-500"}`} />
            <span>{syncing ? "Syncing..." : "Sync Plans"}</span>
          </button>
          <button
            type="button"
            onClick={handlePrint}
            title="Print Summary"
            className="inline-flex items-center gap-1.5 bg-white hover:bg-slate-50 text-slate-700 border border-slate-200 text-xs md:text-sm font-medium py-2 px-3 rounded-lg shadow-xs transition cursor-pointer"
          >
            <FaPrint className="w-3.5 h-3.5 text-slate-500" />
            <span className="hidden md:inline">Print</span>
          </button>
          <button
            type="button"
            onClick={() => setIsContactOpen(true)}
            className="inline-flex items-center gap-1.5 bg-sky-600 hover:bg-sky-700 text-white text-xs md:text-sm font-medium py-2 px-3.5 rounded-lg shadow-sm transition cursor-pointer"
          >
            <BsHeadset className="w-3.5 h-3.5" />
            <span>Support / Upgrade</span>
          </button>
        </div>
      </div>

      {loading ? (
        <div className="py-20 flex flex-col items-center justify-center text-slate-500">
          <svg className="animate-spin h-8 w-8 text-sky-600 mb-3" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
            <path
              className="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
            />
          </svg>
          <p className="text-sm font-medium">Loading subscription details...</p>
        </div>
      ) : (
        <>
          {/* Main Plan Overview Hero Card */}
          <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-slate-900 via-slate-800 to-sky-950 text-white p-6 md:p-8 shadow-xl border border-slate-700">
            {/* Background Glow & Badge */}
            <div className="absolute top-0 right-0 -mt-8 -mr-8 w-64 h-64 rounded-full bg-sky-500/10 blur-3xl pointer-events-none" />
            <div className="absolute bottom-0 right-10 opacity-10 pointer-events-none">
              <FaCrown className="w-48 h-48 text-white" />
            </div>

            <div className="relative z-10 grid grid-cols-1 lg:grid-cols-12 gap-6 items-center">
              {/* Left Column: Plan Title & Details */}
              <div className="lg:col-span-7 space-y-4">
                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-sky-500/20 border border-sky-400/30 text-sky-300 text-xs font-semibold uppercase tracking-wider">
                  <FaCrown className="w-3.5 h-3.5 text-amber-400" />
                  <span>Subscribed Plan</span>
                </div>

                <div>
                  <h2 className="text-2xl md:text-3xl font-extrabold text-white tracking-tight capitalize">
                    {plan?.name || "Standard Clinic Plan"}
                  </h2>
                  <p className="text-slate-300 text-xs md:text-sm mt-1 max-w-xl">
                    {plan?.description || "Full-featured clinical suite with multi-role access, appointment booking, inventory, and vitals management."}
                  </p>
                </div>

                {/* Plan Metadata Tags */}
                <div className="flex flex-wrap gap-2 pt-1">
                  <div className="flex items-center gap-2 bg-slate-800/80 backdrop-blur-md px-3 py-1.5 rounded-lg border border-slate-700 text-xs">
                    <BsBuilding className="text-sky-400 w-3.5 h-3.5" />
                    <span className="text-slate-400">Tenant:</span>
                    <span className="font-mono font-medium text-slate-200">{tenantId}</span>
                    <button
                      type="button"
                      onClick={() => handleCopy(tenantId, "tenant")}
                      title="Copy Tenant ID"
                      className="text-slate-400 hover:text-white transition ml-1 cursor-pointer"
                    >
                      {copiedField === "tenant" ? <BsCheck className="text-emerald-400 w-4 h-4" /> : <BsClipboard className="w-3 h-3" />}
                    </button>
                  </div>

                  <div className="flex items-center gap-2 bg-slate-800/80 backdrop-blur-md px-3 py-1.5 rounded-lg border border-slate-700 text-xs">
                    <BsPerson className="text-emerald-400 w-3.5 h-3.5" />
                    <span className="text-slate-400">Doctor:</span>
                    <span className="font-medium text-slate-200 truncate max-w-[150px]">
                      {doctor?.name || loggedInUser?.name || "Main Doctor"}
                    </span>
                  </div>

                  {doctor?.email && (
                    <div className="flex items-center gap-2 bg-slate-800/80 backdrop-blur-md px-3 py-1.5 rounded-lg border border-slate-700 text-xs">
                      <span className="text-slate-400">Email:</span>
                      <span className="font-medium text-slate-200">{doctor.email}</span>
                    </div>
                  )}
                </div>
              </div>

              {/* Right Column: Pricing & Quick Stats */}
              <div className="lg:col-span-5 bg-white/5 backdrop-blur-md rounded-xl p-5 border border-white/10 flex flex-col justify-between space-y-4">
                <div className="flex items-baseline justify-between border-b border-white/10 pb-3">
                  <div>
                    <span className="text-xs text-slate-300 font-medium">Billing Amount</span>
                    <div className="flex items-baseline gap-1 mt-0.5">
                      <span className="text-2xl md:text-3xl font-bold text-white">
                        {plan?.currency === "INR" || !plan?.currency ? "₹" : plan.currency}{" "}
                        {plan?.price !== undefined ? Number(plan.price).toLocaleString("en-IN") : "Active"}
                      </span>
                      {plan?.billingCycle && (
                        <span className="text-xs text-slate-300 font-normal">/ {plan.billingCycle}</span>
                      )}
                    </div>
                  </div>
                  <div className="text-right">
                    <span className="text-xs text-slate-300 font-medium">Payment Status</span>
                    <div className="flex items-center gap-1.5 text-emerald-400 text-sm font-semibold mt-0.5 justify-end">
                      <MdVerified className="w-4 h-4" />
                      <span>{sub?.payment_id && sub.payment_id !== "N/A" ? "PAID" : "ACTIVE"}</span>
                    </div>
                  </div>
                </div>

                {/* Days Remaining Meter */}
                <div className="space-y-1.5">
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-slate-300 flex items-center gap-1">
                      <BsCalendarCheck className="text-sky-400 w-3.5 h-3.5" />
                      <span>Validity Duration</span>
                    </span>
                    <span className="font-semibold text-white">
                      {sub?.days_remaining !== null && sub?.days_remaining !== undefined
                        ? `${sub?.days_remaining} Days Remaining`
                        : "Active Period"}
                    </span>
                  </div>
                  <div className="w-full bg-slate-700/60 rounded-full h-2 overflow-hidden">
                    <div
                      className={`h-full rounded-full transition-all duration-500 ${
                        progressPercentage > 25 ? "bg-sky-400" : "bg-amber-400"
                      }`}
                      style={{ width: `${progressPercentage}%` }}
                    />
                  </div>
                  <div className="flex justify-between text-[11px] text-slate-400 pt-0.5">
                    <span>From: {formatDate(sub?.valid_from)}</span>
                    <span>Till: {formatDate(sub?.valid_till)}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Key Metrics Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {/* Start Date */}
            <div className="bg-white rounded-xl p-4 border border-slate-200 shadow-xs flex items-center gap-3.5">
              <div className="w-11 h-11 rounded-xl bg-sky-50 text-sky-600 flex items-center justify-center flex-shrink-0 border border-sky-100">
                <FaCalendarAlt className="w-5 h-5" />
              </div>
              <div className="min-w-0">
                <p className="text-xs text-slate-500 font-medium">Subscription Start</p>
                <p className="text-sm font-bold text-slate-800 truncate">{formatDate(sub?.valid_from)}</p>
                <p className="text-[11px] text-slate-400">Activation Date</p>
              </div>
            </div>

            {/* Renewal / Expiry Date */}
            <div className="bg-white rounded-xl p-4 border border-slate-200 shadow-xs flex items-center gap-3.5">
              <div className="w-11 h-11 rounded-xl bg-indigo-50 text-indigo-600 flex items-center justify-center flex-shrink-0 border border-indigo-100">
                <BsCalendarCheck className="w-5 h-5" />
              </div>
              <div className="min-w-0">
                <p className="text-xs text-slate-500 font-medium">Valid Until / Expiry</p>
                <p className="text-sm font-bold text-slate-800 truncate">{formatDate(sub?.valid_till)}</p>
                <p className="text-[11px] text-slate-400">
                  {sub?.days_remaining !== null ? `${sub?.days_remaining} days left` : "Annual renewal"}
                </p>
              </div>
            </div>

            {/* Enabled Modules */}
            <div className="bg-white rounded-xl p-4 border border-slate-200 shadow-xs flex items-center gap-3.5">
              <div className="w-11 h-11 rounded-xl bg-emerald-50 text-emerald-600 flex items-center justify-center flex-shrink-0 border border-emerald-100">
                <BsLightningChargeFill className="w-5 h-5" />
              </div>
              <div className="min-w-0">
                <p className="text-xs text-slate-500 font-medium">Modules Included</p>
                <p className="text-sm font-bold text-slate-800">
                  {enabledCount} <span className="text-xs text-slate-500 font-normal">/ {PLAN_SECTIONS.length}</span>
                </p>
                <p className="text-[11px] text-emerald-600 font-medium">Active Features</p>
              </div>
            </div>

            {/* Security / Status */}
            <div className="bg-white rounded-xl p-4 border border-slate-200 shadow-xs flex items-center gap-3.5">
              <div className="w-11 h-11 rounded-xl bg-amber-50 text-amber-600 flex items-center justify-center flex-shrink-0 border border-amber-100">
                <MdOutlineSecurity className="w-5 h-5" />
              </div>
              <div className="min-w-0">
                <p className="text-xs text-slate-500 font-medium">Security & Status</p>
                <p className="text-sm font-bold text-slate-800 truncate capitalize">{status}</p>
                <p className="text-[11px] text-slate-400">Multi-tenant Isolation</p>
              </div>
            </div>
          </div>

          {/* Payment & Order Reference Details Card */}
          <div className="bg-white rounded-xl border border-slate-200 shadow-xs p-5 md:p-6">
            <div className="flex items-center justify-between border-b border-slate-100 pb-3 mb-4">
              <div className="flex items-center gap-2">
                <BsCreditCard className="w-4 h-4 text-sky-600" />
                <h3 className="text-base font-bold text-slate-800">Billing & Payment References</h3>
              </div>
              <span className="text-xs text-slate-500">Razorpay Gateway Integration</span>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {/* Order ID */}
              <div className="p-3.5 rounded-lg bg-slate-50 border border-slate-200/80">
                <span className="text-xs text-slate-500 font-medium block mb-1">Razorpay Order ID</span>
                <div className="flex items-center justify-between gap-2">
                  <span className="font-mono text-xs font-semibold text-slate-800 truncate">
                    {sub?.subscription_id || "N/A"}
                  </span>
                  {sub?.subscription_id && sub.subscription_id !== "N/A" && (
                    <button
                      type="button"
                      onClick={() => handleCopy(sub.subscription_id, "orderId")}
                      title="Copy Order ID"
                      className="p-1 hover:bg-slate-200 rounded text-slate-500 hover:text-slate-800 transition cursor-pointer"
                    >
                      {copiedField === "orderId" ? <BsCheck className="text-emerald-500 w-4 h-4" /> : <BsClipboard className="w-3.5 h-3.5" />}
                    </button>
                  )}
                </div>
              </div>

              {/* Payment ID */}
              <div className="p-3.5 rounded-lg bg-slate-50 border border-slate-200/80">
                <span className="text-xs text-slate-500 font-medium block mb-1">Razorpay Payment ID</span>
                <div className="flex items-center justify-between gap-2">
                  <span className="font-mono text-xs font-semibold text-slate-800 truncate">
                    {sub?.payment_id || "N/A"}
                  </span>
                  {sub?.payment_id && sub.payment_id !== "N/A" && (
                    <button
                      type="button"
                      onClick={() => handleCopy(sub.payment_id, "paymentId")}
                      title="Copy Payment ID"
                      className="p-1 hover:bg-slate-200 rounded text-slate-500 hover:text-slate-800 transition cursor-pointer"
                    >
                      {copiedField === "paymentId" ? <BsCheck className="text-emerald-500 w-4 h-4" /> : <BsClipboard className="w-3.5 h-3.5" />}
                    </button>
                  )}
                </div>
              </div>

              {/* Plan Identifier */}
              <div className="p-3.5 rounded-lg bg-slate-50 border border-slate-200/80">
                <span className="text-xs text-slate-500 font-medium block mb-1">Plan Identifier</span>
                <div className="flex items-center justify-between gap-2">
                  <span className="font-mono text-xs font-semibold text-slate-800 truncate">
                    {plan?.plan_id || "plan_standard"}
                  </span>
                  <button
                    type="button"
                    onClick={() => handleCopy(plan?.plan_id || "plan_standard", "planId")}
                    title="Copy Plan ID"
                    className="p-1 hover:bg-slate-200 rounded text-slate-500 hover:text-slate-800 transition cursor-pointer"
                  >
                    {copiedField === "planId" ? <BsCheck className="text-emerald-500 w-4 h-4" /> : <BsClipboard className="w-3.5 h-3.5" />}
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Enabled Modules & Plan Features Matrix */}
          <div className="bg-white rounded-xl border border-slate-200 shadow-xs p-5 md:p-6 space-y-4">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 border-b border-slate-100 pb-3">
              <div>
                <div className="flex items-center gap-2">
                  <FaCrown className="w-4 h-4 text-amber-500" />
                  <h3 className="text-base font-bold text-slate-800">Plan Feature & Module Access</h3>
                </div>
                <p className="text-xs text-slate-500 mt-0.5">
                  Review which clinical modules, pages, and capabilities are configured for your plan.
                </p>
              </div>

              {/* Feature Search Box */}
              <div className="relative min-w-[220px]">
                <BsSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 w-3.5 h-3.5" />
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Search modules..."
                  className="w-full pl-8 pr-3 py-1.5 text-xs bg-slate-50 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-sky-500 focus:bg-white transition"
                />
              </div>
            </div>

            {/* Category Filter Pills */}
            <div className="flex flex-wrap gap-1.5 pt-1">
              {categories.map((cat) => (
                <button
                  key={cat}
                  type="button"
                  onClick={() => setSelectedCategory(cat)}
                  className={`px-3 py-1 rounded-lg text-xs font-medium transition cursor-pointer ${
                    selectedCategory === cat
                      ? "bg-slate-800 text-white shadow-xs"
                      : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                  }`}
                >
                  {cat}
                </button>
              ))}
            </div>

            {/* Features Grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2.5 pt-2 max-h-[460px] overflow-y-auto pr-1">
              {filteredSections.map((sec) => {
                const isEnabled = enabledSections.includes(sec.value);
                return (
                  <div
                    key={sec.value}
                    className={`flex items-center justify-between p-3 rounded-lg border transition ${
                      isEnabled
                        ? "bg-emerald-50/50 border-emerald-200/80 text-slate-800"
                        : "bg-slate-50/50 border-slate-200/70 text-slate-500"
                    }`}
                  >
                    <div className="min-w-0 pr-2">
                      <p className={`text-xs font-semibold truncate ${isEnabled ? "text-slate-900" : "text-slate-500"}`}>
                        {sec.label}
                      </p>
                      <p className="text-[10px] text-slate-400 font-mono truncate">{sec.value}</p>
                    </div>

                    <div className="flex-shrink-0">
                      {isEnabled ? (
                        <span
                          title="Active in your plan"
                          className="inline-flex items-center gap-1 text-[11px] font-semibold text-emerald-700 bg-emerald-100/80 px-2 py-0.5 rounded-full"
                        >
                          <BsCheckCircleFill className="w-3 h-3 text-emerald-600" />
                          <span className="hidden sm:inline">Active</span>
                        </span>
                      ) : (
                        <span
                          title="Not enabled for current plan"
                          className="inline-flex items-center gap-1 text-[11px] font-normal text-slate-400 bg-slate-200/60 px-2 py-0.5 rounded-full"
                        >
                          <MdOutlineLock className="w-3 h-3 text-slate-400" />
                          <span className="hidden sm:inline">Locked</span>
                        </span>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>

            {filteredSections.length === 0 && (
              <div className="text-center py-8 text-slate-400 text-xs">
                No matching modules found for "{searchTerm}".
              </div>
            )}
          </div>

          {/* Need Changes or Custom Tier Banner */}
          <div className="rounded-xl border border-sky-200 bg-gradient-to-r from-sky-50 to-indigo-50 p-5 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <div className="flex items-start gap-3">
              <div className="w-9 h-9 rounded-lg bg-sky-600 text-white flex items-center justify-center flex-shrink-0 shadow-xs">
                <BsInfoCircle className="w-5 h-5" />
              </div>
              <div>
                <h4 className="text-sm font-bold text-slate-900">Need to modify or expand your subscription?</h4>
                <p className="text-xs text-slate-600 mt-0.5">
                  Contact our clinic support team to unlock additional features, add practitioner accounts, or request specialized custom modules.
                </p>
              </div>
            </div>
            <button
              type="button"
              onClick={() => setIsContactOpen(true)}
              className="inline-flex items-center gap-2 bg-slate-900 hover:bg-slate-800 text-white text-xs font-semibold py-2.5 px-4 rounded-lg shadow-sm transition whitespace-nowrap cursor-pointer"
            >
              <BsHeadset className="w-3.5 h-3.5" />
              <span>Contact Support</span>
            </button>
          </div>
        </>
      )}

      {/* Support / Contact Us Modal */}
      <ContactUsPopup isOpen={isContactOpen} onClose={() => setIsContactOpen(false)} />
      <ToastContainer />
    </div>
  );

  if (embedded) {
    return content;
  }

  return (
    <RightWrapper>
      <div className="py-2 px-2 md:px-4 w-full max-w-5xl mx-auto">{content}</div>
    </RightWrapper>
  );
};

export default SubscriptionPlanView;
